import React, { useState, useEffect } from 'react';
import { ArrowLeft, Crown, History, Grid } from 'lucide-react';
import { API_URL } from '../utils/gameUtils';

export default function RankingScreen({ onBack }) {
    const [ranking, setRanking] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedIndex, setExpandedIndex] = useState(null);
    const [selectedTopic, setSelectedTopic] = useState(null); // null means "All"
    const [showTopicGrid, setShowTopicGrid] = useState(false);

    // ヒラガナを行ごとに分割（5列）
    const hiraganaRows = [
        ['あ', 'い', 'う', 'え', 'お'],
        ['か', 'き', 'く', 'け', 'こ'],
        ['さ', 'し', 'す', 'せ', 'そ'],
        ['た', 'ち', 'つ', 'て', 'と'],
        ['な', 'に', 'ぬ', 'ね', 'の'],
        ['は', 'ひ', 'ふ', 'へ', 'ほ'],
        ['ま', 'み', 'む', 'め', 'も'],
        ['や', '', 'ゆ', '', 'よ'],
        ['ら', 'り', 'る', 'れ', 'ろ'],
        ['わ', '', '', '', '']
    ];

    useEffect(() => {
        const fetchRanking = async () => {
            setLoading(true);
            try {
                let url = `${API_URL}/ranking?limit=10`;
                if (selectedTopic) {
                    url += `&topic=${selectedTopic}`;
                }
                const res = await fetch(url);
                if (res.ok) {
                    const data = await res.json();
                    setRanking(data);
                } else {
                    setRanking([]);
                }
            } catch (e) {
                console.error("Failed to fetch ranking", e);
                setRanking([]);
            } finally {
                setLoading(false);
            }
        };
        fetchRanking();
        setExpandedIndex(null); // Reset expansion on topic change
    }, [selectedTopic]);

    return (
        <div className="space-y-4 animate-fade-in h-full flex flex-col bg-orange-50/50">
            {/* Header */}
            <div className="flex items-center justify-between pb-2 border-b border-orange-100 bg-white/80 backdrop-blur-sm sticky top-0 z-10 px-2 py-3 rounded-xl shadow-sm">
                <div className="flex items-center gap-2">
                    <button onClick={onBack} className="p-2 hover:bg-orange-100 rounded-full transition-colors group">
                        <ArrowLeft className="w-5 h-5 text-gray-500 group-hover:text-orange-600 transition-colors" />
                    </button>
                    <h2 className="text-xl font-bold flex items-center gap-2 text-orange-600">
                        <Crown className="w-6 h-6 fill-current text-yellow-400 drop-shadow-sm" />
                        ランキング
                    </h2>
                </div>
                <button
                    onClick={() => setShowTopicGrid(!showTopicGrid)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-bold transition-all ${selectedTopic
                        ? 'bg-orange-100 text-orange-700 hover:bg-orange-200 ring-2 ring-orange-200'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                >
                    <Grid className="w-4 h-4" />
                    {selectedTopic ? `お題: ${selectedTopic}` : '総合ランキング'}
                </button>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-hidden relative">
                {/* Topic Selector Overlay */}
                <div className={`absolute inset-0 bg-white z-20 overflow-y-auto transition-transform duration-300 ease-in-out ${showTopicGrid ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 pointer-events-none'}`}>
                    <div className="p-4 safe-area-bottom">
                        <h3 className="text-center font-bold text-gray-500 mb-4 sticky top-0 bg-white py-2">お題を選択してください</h3>
                        <div className="grid grid-cols-5 gap-2 max-w-sm mx-auto mb-8">
                            <button
                                onClick={() => {
                                    setSelectedTopic(null);
                                    setShowTopicGrid(false);
                                }}
                                className={`col-span-5 py-3 rounded-lg font-bold transition-all shadow-sm ${selectedTopic === null
                                    ? 'bg-orange-500 text-white ring-2 ring-orange-300 ring-offset-2'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                            >
                                全て
                            </button>
                            {hiraganaRows.map((row, rowIndex) => (
                                <React.Fragment key={rowIndex}>
                                    {row.map((char, charIndex) => (
                                        char ? (
                                            <button
                                                key={`${rowIndex}-${charIndex}`}
                                                onClick={() => {
                                                    setSelectedTopic(char);
                                                    setShowTopicGrid(false);
                                                }}
                                                className={`aspect-square rounded-lg font-bold text-lg transition-all shadow-sm flex items-center justify-center ${selectedTopic === char
                                                    ? 'bg-orange-500 text-white ring-2 ring-orange-300 ring-offset-2 scale-105'
                                                    : 'bg-white border-2 border-orange-100 text-orange-800 hover:bg-orange-50 hover:border-orange-300'
                                                    }`}
                                            >
                                                {char}
                                            </button>
                                        ) : (
                                            <div key={`${rowIndex}-${charIndex}`} className="aspect-square" />
                                        )
                                    ))}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Ranking List */}
                <div className="h-full overflow-y-auto pr-1 pb-10">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20 gap-4 text-orange-400">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-current"></div>
                            <p className="text-sm font-medium animate-pulse">ランキング読み込み中...</p>
                        </div>
                    ) : ranking.length === 0 ? (
                        <div className="text-center py-20 text-gray-400">
                            <History className="w-12 h-12 mx-auto mb-3 opacity-20" />
                            <p className="font-bold text-gray-500">データがありません</p>
                            <p className="text-xs mt-1">まだこのお題での記録はありません</p>
                        </div>
                    ) : (
                        <div className="space-y-3 p-1">
                            {ranking.map((item, index) => (
                                <div key={index} className="group">
                                    <div
                                        onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                                        className={`relative flex items-center p-3 cursor-pointer transition-all duration-300 border ${expandedIndex === index
                                            ? 'rounded-t-2xl border-b-0 bg-white border-orange-300 shadow-md z-10'
                                            : 'rounded-2xl shadow-sm hover:shadow-md'
                                            } ${expandedIndex !== index && index === 0 ? 'bg-gradient-to-r from-yellow-50 to-white border-yellow-200 shadow-yellow-100' :
                                                expandedIndex !== index && index === 1 ? 'bg-gradient-to-r from-gray-50 to-white border-gray-200' :
                                                    expandedIndex !== index && index === 2 ? 'bg-gradient-to-r from-orange-50 to-white border-orange-200' :
                                                        expandedIndex !== index ? 'bg-white border-orange-50' : ''
                                            }`}
                                    >
                                        {/* Rank Badge */}
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-lg mr-3 shadow-sm border-2 ${index === 0 ? 'bg-yellow-400 text-white border-yellow-300 ring-2 ring-yellow-100' :
                                            index === 1 ? 'bg-gray-400 text-white border-gray-300 ring-2 ring-gray-100' :
                                                index === 2 ? 'bg-orange-400 text-white border-orange-300 ring-2 ring-orange-100' : 'bg-slate-100 text-slate-400 border-slate-200'
                                            }`}>
                                            {index + 1}
                                        </div>

                                        {/* User Info */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <div className="text-base font-bold text-gray-800 truncate">{item.user_name || "Guest"}</div>
                                                {item.topic && (
                                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-100 text-orange-600 border border-orange-200">
                                                        {item.topic}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-gray-400 font-medium flex items-center gap-1">
                                                {new Date(item.created_at).toLocaleDateString()}
                                            </div>
                                        </div>

                                        {/* Score */}
                                        <div className="text-right pl-2">
                                            <div className="text-xl font-black text-orange-600 tabular-nums tracking-tight">
                                                {item.score}
                                            </div>
                                            <div className="text-[10px] font-bold text-gray-400 -mt-1 uppercase tracking-wide">Points</div>
                                        </div>
                                    </div>

                                    {/* Expanded Details */}
                                    <div className={`
                                        overflow-hidden transition-all duration-300 ease-in-out bg-white
                                        ${expandedIndex === index ? 'max-h-96 opacity-100 rounded-b-2xl border-x border-b border-orange-300 shadow-md mb-4' : 'max-h-0 opacity-0 mb-0'}
                                    `}>
                                        <div className="p-4 pt-2">
                                            <div className="flex items-center gap-2 mb-3 text-orange-800/60 text-xs font-bold uppercase tracking-wider border-b border-orange-100 pb-2">
                                                <History className="w-3 h-3" />
                                                History ({item.words_history ? item.words_history.split(',').filter(w => w).length : 0} words)
                                            </div>
                                            <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto pr-1">
                                                {item.words_history && item.words_history.split(',').filter(w => w).map((word, i) => (
                                                    <span
                                                        key={i}
                                                        className="bg-orange-50 px-3 py-1.5 rounded-lg text-sm font-medium border border-orange-100 text-orange-800 shadow-sm transition-all hover:bg-orange-100 hover:scale-105 cursor-default select-none"
                                                    >
                                                        {word}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
